import { t } from 'i18next';

import {
    logisticsCasePriorityInputValues,
    logisticsCasePriorityOutputValues,
} from './logistic-management/LogisticManagementPriority';

// Priority values are shared across logistics_cases; kept as aliases for back-compat.
export const additionalRequestPriorityOutputValues = logisticsCasePriorityOutputValues;
export const additionalRequestPriorityInputValues = logisticsCasePriorityInputValues;

export const AdditionalRequestStatusEnum = Object.freeze({
    OPEN: 'OPEN',
    VALIDATION_REQUESTED: 'VALIDATION_REQUESTED',
    VALIDATION_CONFIRMED: 'VALIDATION_CONFIRMED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
});

type AdditionalRequestRaw = {
    publicId: string;
    iid: string;
    status: keyof typeof AdditionalRequestStatusEnum;
    priority: keyof typeof additionalRequestPriorityOutputValues;
    assignedTo: {
        id: string;
        type: string;
        role?: string;
        firstName?: string;
        lastName?: string;
        company?: string;
        customer?: { id: string; company: string };
        logisticProvider?: { id: string; company: string };
    } | null;
    targetDate?: string;
    resolutionDate?: string;
    lastCommenter: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    creator: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    closer: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    canceler: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    additionalRequestType?: { typeName: string; typeCustomerCode?: string; typeUniqueIdentifier: string };
    additionalRequestSubType?: { subTypeName: string; subTypeCustomerCode?: string; subTypeUniqueIdentifier: string };
    additionalRequestServiceFee?: number;
    additionalRequestFeeUnit?: string;
    additionalRequestServiceType?: string;
    content?: {
        description?: string | null;
        files?: { url: string; name: string }[];
    };
};

export type AdditionalRequest = {
    publicId: string;
    iid: string;
    status: keyof typeof AdditionalRequestStatusEnum;
    priority: keyof typeof additionalRequestPriorityOutputValues;
    assignedTo: {
        id: string;
        type: string;
        role?: string;
        firstName?: string;
        lastName?: string;
        company?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    targetDate?: string;
    resolutionDate?: string;
    lastCommenter: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    creator: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    closer: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;
    canceler: {
        id: string;
        firstName: string;
        lastName: string;
        role?: string;
        customer?: { company: string };
        logisticProvider?: { company: string };
    } | null;

    // this value is set by normalizeAdditionalRequest
    type: { name: string; customerCode?: string; uniqueIdentifier: string } | null;
    subType: { name: string; customerCode?: string; uniqueIdentifier: string } | null;
    serviceFee: number | null;
    feeUnit: string | null;
    serviceType: string | null;
    description: string | null;
    files: { url: string; name: string }[];
};

export function getAdditionalRequestsStatusesOptions() {
    const options = {
        [AdditionalRequestStatusEnum.OPEN]: {
            id: AdditionalRequestStatusEnum.OPEN,
            key: AdditionalRequestStatusEnum.OPEN,
            label: t('Nouveau'),
        },
        [AdditionalRequestStatusEnum.VALIDATION_REQUESTED]: {
            id: AdditionalRequestStatusEnum.VALIDATION_REQUESTED,
            key: AdditionalRequestStatusEnum.VALIDATION_REQUESTED,
            label: t('Pending validation'),
        },
        [AdditionalRequestStatusEnum.VALIDATION_CONFIRMED]: {
            id: AdditionalRequestStatusEnum.VALIDATION_CONFIRMED,
            key: AdditionalRequestStatusEnum.VALIDATION_CONFIRMED,
            label: t('Processing in progress'),
        },
        [AdditionalRequestStatusEnum.COMPLETED]: {
            id: AdditionalRequestStatusEnum.COMPLETED,
            key: AdditionalRequestStatusEnum.COMPLETED,
            label: t('Finished'),
        },
        [AdditionalRequestStatusEnum.CANCELLED]: {
            id: AdditionalRequestStatusEnum.CANCELLED,
            key: AdditionalRequestStatusEnum.CANCELLED,
            label: t('Canceled'),
        },
    };
    return options;
}
export function getAdditionalRequestsStatusLabel(key: string) {
    const options = getAdditionalRequestsStatusesOptions();
    return options?.[key as keyof typeof options]?.label || '';
}

export function getAdditionalRequestsServiceTypesOptions() {
    const labels = {
        INCLUDED: { value: 'INCLUDED', label: t('Included in the contract') },
        BILLABLE: { value: 'BILLABLE', label: t('Billable') },
    };
    return labels;
}

export function getAdditionalRequestsServiceTypeLabel(key: string) {
    const options = getAdditionalRequestsServiceTypesOptions();
    return options?.[key as keyof typeof options]?.label || '';
}

export function getAdditionalRequestsServiceFeeUnitOptions() {
    const labels = {
        EUR: { value: 'EUR', text: '€' },
        HOUR: { value: 'HOUR', text: t('hours') },
        DAY: { value: 'DAY', text: t('days') },
        BOX: { value: 'BOX', text: t('boxes') },
        PALLET: { value: 'PALLET', text: t('pallets') },
        UNIT: { value: 'UNIT', text: t('units') },
    };
    return labels;
}

export function getAdditionalRequestsServiceFeeUnitLabel(key: string) {
    const options = getAdditionalRequestsServiceFeeUnitOptions();
    return options?.[key as keyof typeof options]?.text || '';
}

export function normalizeAdditionalRequest(data: AdditionalRequestRaw): AdditionalRequest {
    const {
        additionalRequestType,
        additionalRequestSubType,
        additionalRequestServiceType,
        additionalRequestServiceFee,
        additionalRequestFeeUnit,
        content,
        ...rest
    } = data;
    const newResponse: AdditionalRequest = {
        ...rest,
        description: content?.description ?? null,
        files: content?.files ?? [],
        type: additionalRequestType
            ? {
                  name: additionalRequestType.typeName,
                  customerCode: additionalRequestType.typeCustomerCode,
                  uniqueIdentifier: additionalRequestType.typeUniqueIdentifier,
              }
            : null,
        subType: additionalRequestSubType
            ? {
                  name: additionalRequestSubType.subTypeName,
                  customerCode: additionalRequestSubType.subTypeCustomerCode,
                  uniqueIdentifier: additionalRequestSubType.subTypeUniqueIdentifier,
              }
            : null,
        serviceType: additionalRequestServiceType || null,
        serviceFee: additionalRequestServiceFee || null,
        feeUnit: additionalRequestFeeUnit || null,
        priority:
            additionalRequestPriorityInputValues[data.priority as keyof typeof additionalRequestPriorityInputValues],
    };
    return newResponse;
}

export function normalizeAdditionalRequestType(
    type: AdditionalRequestRaw['additionalRequestType'] & {
        id: string;
        content?: { description?: string };
        subTypes: { items: AdditionalRequestRaw['additionalRequestSubType'][] };
    }
) {
    const { id, typeName, typeUniqueIdentifier, typeCustomerCode, content, subTypes, ...rest } = type || {};
    return {
        id,
        name: typeName,
        customerCode: typeCustomerCode,
        uniqueIdentifier: typeUniqueIdentifier,
        descriptionTemplate: content?.description ?? '',
        subRows: (subTypes?.items || []).map((subRow) => ({
            id: subRow?.subTypeUniqueIdentifier,
            name: subRow?.subTypeName,
            customerCode: subRow?.subTypeCustomerCode,
            uniqueIdentifier: subRow?.subTypeUniqueIdentifier,
        })),
        ...rest,
    };
}

export function normalizeAdditionalRequestSubType(
    subType: AdditionalRequestRaw['additionalRequestSubType'] & {
        id: string;
    }
) {
    const { id, subTypeName, subTypeUniqueIdentifier, subTypeCustomerCode } = subType || {};
    return {
        id,
        name: subTypeName,
        customerCode: subTypeCustomerCode,
        uniqueIdentifier: subTypeUniqueIdentifier,
    };
}

export function getDefaultTypeWithSubtypes(keyFilter?: string) {
    const options = [
        {
            uniqueIdentifier: 'QUALITY_CONTROL',
            id: 'QUALITY_CONTROL',
            name: t('Quality control'),
            isDefault: true,
            subRows: [
                { uniqueIdentifier: 'VISUAL_INSPECTION', id: 'VISUAL_INSPECTION', name: t('Visual inspection') },
                {
                    uniqueIdentifier: 'QUANTITATIVE_CHECK',
                    id: 'QUANTITATIVE_CHECK',
                    name: t('Quantitative check'),
                },
                { uniqueIdentifier: 'COMPLIANCE_CHECK', id: 'COMPLIANCE_CHECK', name: t('Compliance check') },
                { uniqueIdentifier: 'SPECIFIC_CHECK', id: 'SPECIFIC_CHECK', name: t('Specific check') },
            ],
        },
        {
            uniqueIdentifier: 'TARGETED_INVENTORY',
            id: 'TARGETED_INVENTORY',
            name: t('Targeted inventory'),
            isDefault: true,
            subRows: [
                { uniqueIdentifier: 'BY_LOCATION', id: 'BY_LOCATION', name: t('Invertory by location') },
                {
                    uniqueIdentifier: 'BY_REFERENCE_SKU',
                    id: 'BY_REFERENCE_SKU',
                    name: t('Invertory by reference or sku'),
                },
                {
                    uniqueIdentifier: 'BY_BATCH_EXPIRY',
                    id: 'BY_BATCH_EXPIRY',
                    name: t('Invertory by batch expiry'),
                },
                { uniqueIdentifier: 'BY_SSCC', id: 'BY_SSCC', name: t('Invertory by sscc') },
                {
                    uniqueIdentifier: 'INVENTORY_FOLLOWING_A_DISPUTE',
                    id: 'INVENTORY_FOLLOWING_A_DISPUTE',
                    name: t('Inventory following a dispute'),
                },
            ],
        },
        {
            uniqueIdentifier: 'RE_LABELING',
            id: 'RE_LABELING',
            name: t('Re-labelling'),
            isDefault: true,
            subRows: [
                {
                    uniqueIdentifier: 'REPLACEMENT_OF_LABELS',
                    id: 'REPLACEMENT_OF_LABELS',
                    name: t('Replacement of labels'),
                },
                {
                    uniqueIdentifier: 'ADDING_OF_MISSING_LABELS',
                    id: 'ADDING_OF_MISSING_LABELS',
                    name: t('Adding of missing labels'),
                },
                {
                    uniqueIdentifier: 'LABELLING_CORRECTION',
                    id: 'LABELLING_CORRECTION',
                    name: t('Labelling correction (barcodes, names)'),
                },
                {
                    uniqueIdentifier: 'ADDING_REGULATORY_LABELS',
                    id: 'ADDING_REGULATORY_LABELS',
                    name: t('Adding regulatory/country labels'),
                },
            ],
        },
        {
            uniqueIdentifier: 'REPACKAGING',
            id: 'REPACKAGING',
            name: t('Repackaging'),
            isDefault: true,
            subRows: [
                { uniqueIdentifier: 'SORTING_SEPARATE', id: 'SORTING_SEPARATE', name: t('Sorting/separation') },
                {
                    uniqueIdentifier: 'DECONSTITUTION_RECOMPOSITION',
                    id: 'DECONSTITUTION_RECOMPOSITION',
                    name: t('Deconstitution/recomposition'),
                },
                { uniqueIdentifier: 'NEW_FORMAT', id: 'NEW_FORMAT', name: t('Repackaging in a new format') },
                {
                    uniqueIdentifier: 'ADDING_REMOVE_ITEMS',
                    id: 'ADDING_REMOVE_ITEMS',
                    name: t('Adding/removing items'),
                },
            ],
        },
        {
            uniqueIdentifier: 'RE_PALLETIZING',
            id: 'RE_PALLETIZING',
            name: t('Re-palletizing'),
            isDefault: true,
            subRows: [
                { uniqueIdentifier: 'EURO_PALLET', id: 'EURO_PALLET', name: t('Re-palletising European pallets') },
                { uniqueIdentifier: 'LOST_PALLET', id: 'LOST_PALLET', name: t('Re-palletising of lost pallets') },
                {
                    uniqueIdentifier: 'HALF_PALLET_ROLL',
                    id: 'HALF_PALLET_ROLL',
                    name: t('Half pallet passage / roll '),
                },
                {
                    uniqueIdentifier: 'CONSOLIDATE_DECONSOLIDATE',
                    id: 'CONSOLIDATE_DECONSOLIDATE',
                    name: t('Consolidating or deconsolidating'),
                },
            ],
        },
        {
            uniqueIdentifier: 'PHOTO_CAPTURE',
            id: 'PHOTO_CAPTURE',
            name: t('Photo capture'),
            isDefault: true,
            subRows: [
                { uniqueIdentifier: 'PRODUCT_PHOTOS', id: 'PRODUCT_PHOTOS', name: t('Product photos') },
                {
                    uniqueIdentifier: 'PALLET_SUPPORT_PHOTOS',
                    id: 'PALLET_SUPPORT_PHOTOS',
                    name: t('Pallet support photos'),
                },
                {
                    uniqueIdentifier: 'DISPUTE_DAMAGE_PHOTOS',
                    id: 'DISPUTE_DAMAGE_PHOTOS',
                    name: t('Dispute damage photos'),
                },
                { uniqueIdentifier: 'PHOTO_REPORT', id: 'PHOTO_REPORT', name: t('Photo report') },
            ],
        },
    ];
    if (keyFilter) {
        return options.filter((o) => o.uniqueIdentifier === keyFilter);
    }
    return options;
}

export function getDefaultSubtypes(keyFilter?: string) {
    const subKeys = getDefaultTypeWithSubtypes(keyFilter).reduce((previousValue, current) => {
        return [...previousValue, ...current.subRows];
    }, []);
    return subKeys;
}

export function getAdditionalRequestsTypeLabel(key: string) {
    const options = getDefaultTypeWithSubtypes();
    return options?.find((item) => item.uniqueIdentifier === key)?.name || '';
}

export function getAdditionalRequestsSubTypeLabel(subKey: string) {
    const options = getDefaultTypeWithSubtypes();
    const subTypes = options?.reduce((subRows, item) => {
        return [...subRows, ...item.subRows];
    }, []);

    if (!subTypes?.length) {
        return '';
    }
    return subTypes?.find((item) => item.uniqueIdentifier === subKey)?.name || '';
}
